import React, { useMemo } from "react";
import Card from "../common/Card";
import Icon from "../common/Icon";
import Tooltip from "./Tooltip";
import { METRIC_TOOLTIPS } from "../../utils/constants";
import { exportToCSV } from "../../utils/exportUtils";
import "./PeopleAlsoAskReport.css";

const buildAggregationKey = (category, keyword) => {
  const normalizedCategory = category ? category.toLowerCase().trim() : "";
  const normalizedKeyword = keyword ? keyword.toLowerCase().trim() : "";
  return `${normalizedCategory}|${normalizedKeyword}`;
};

const aggregateRowsByCategoryAndKeyword = (keywords) => {
  const rowsByKey = new Map();

  keywords.forEach((kw) => {
    if (!kw.keyword) return;

    const aggregationKey = buildAggregationKey(kw.category, kw.keyword);
    const questions = kw.paaQuestions || [];
    const existingRow = rowsByKey.get(aggregationKey);

    if (!existingRow) {
      rowsByKey.set(aggregationKey, {
        category: kw.category || null,
        keyword: kw.keyword,
        questions: [...questions],
      });
      return;
    }

    questions.forEach((question) => {
      const isDuplicate = existingRow.questions.includes(question);
      if (!isDuplicate) {
        existingRow.questions.push(question);
      }
    });
  });

  const aggregatedRows = rowsByKey.values();
  return Array.from(aggregatedRows);
};

const hasNeverBeenCollected = (keywords) => {
  if (!keywords || keywords.length === 0) return false;
  return keywords.every((kw) => kw.paaQuestions === null || kw.paaQuestions === undefined);
};

const PeopleAlsoAskReport = ({ keywords }) => {
  const aggregatedRows = useMemo(
    () => aggregateRowsByCategoryAndKeyword(keywords || []),
    [keywords]
  );

  const rowsWithQuestions = useMemo(
    () => aggregatedRows.filter((row) => row.questions.length > 0),
    [aggregatedRows]
  );

  const wasNeverCollected = useMemo(() => hasNeverBeenCollected(keywords), [keywords]);

  const hasCategories = useMemo(
    () => aggregatedRows.some((row) => row.category && row.category.trim() !== ""),
    [aggregatedRows]
  );

  const handleExportCSV = () => {
    const exportData = rowsWithQuestions.map((row) => {
      const questionsText = row.questions.join(" | ");
      let baseRow = {
        "Mot-clé": row.keyword,
        "Questions posées": questionsText,
      };
      if (hasCategories) {
        baseRow = { Catégorie: row.category || "-", ...baseRow };
      }
      return baseRow;
    });

    const today = new Date().toISOString().split("T")[0];
    exportToCSV(exportData, `people-also-ask-${today}`);
  };

  const renderTitle = (suffix) => (
    <div className="paa-title-row">
      <h3 className="table-title">People Also Ask{suffix}</h3>
      <Tooltip text={METRIC_TOOLTIPS.PEOPLE_ALSO_ASK}>
        <Icon name="Info" size={14} />
      </Tooltip>
    </div>
  );

  if (wasNeverCollected) {
    return (
      <Card className="people-also-ask-report">
        {renderTitle("")}
        <p className="table-empty">
          Les questions ne sont pas disponibles pour cette analyse. Relancez-la pour les collecter.
        </p>
      </Card>
    );
  }

  if (rowsWithQuestions.length === 0) {
    return (
      <Card className="people-also-ask-report">
        {renderTitle(` (0 sur ${aggregatedRows.length})`)}
        <p className="table-empty">Aucune donnée disponible</p>
      </Card>
    );
  }

  return (
    <Card className="people-also-ask-report">
      <div className="table-header-with-actions">
        {renderTitle(` (${rowsWithQuestions.length} sur ${aggregatedRows.length})`)}
        <div className="header-actions">
          <button className="export-button" onClick={handleExportCSV}>
            <Icon name="Download" size={16} />
            Exporter CSV
          </button>
        </div>
      </div>

      <div className="paa-table-wrapper">
        <table className="paa-table">
          <thead>
            <tr>
              {hasCategories && <th>Catégorie</th>}
              <th>Mot-clé</th>
              <th>Questions posées</th>
            </tr>
          </thead>
          <tbody>
            {rowsWithQuestions.map((row) => (
              <tr key={buildAggregationKey(row.category, row.keyword)}>
                {hasCategories && (
                  <td className="category-cell">
                    {row.category ? (
                      <span className="category-badge">{row.category}</span>
                    ) : (
                      <span className="category-none">-</span>
                    )}
                  </td>
                )}
                <td className="paa-keyword-cell">{row.keyword}</td>
                <td>
                  <ul className="paa-questions-list">
                    {row.questions.map((question) => (
                      <li key={question}>{question}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default PeopleAlsoAskReport;
